import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties — curated nearby communities under the same management.
 *
 * There is no portfolio collection in Vibesites yet, so each community is a
 * fixed slot group editors fill manually (image, name, location, link).
 * Empty slots are hidden on the live site and shown as dashed placeholders
 * in edit mode so operators can see exactly where to click.
 */

// ─── Slot group definitions (one literal per community — the schema extractor
//     follows one level of module-level SlotGroupDefinition constants but not
//     factories, so we repeat the shape rather than build them in a loop) ─────

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/39-9395%20scarborough%20st%20-30_final.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Sunlit open living space with dining table and white kitchen island at Prairie Creek',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 1 of 6 on Sister Properties. Image and name are set on this card — describe that photo only, not other sister communities.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Prairie Creek Apartments & Townhomes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '9395 Scarborough St, Lenexa, KS 66219',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.prairiecreekapartments.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text. Prefer real Price Brothers / company portfolio communities.',
    context:
      'Card 1 of 6 on Sister Properties — nearest neighbor first. Do not reuse communities chosen for cards 2–6. Exclude this property itself.',
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/02-8500%20maurer%20rd-2_final.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Living room looking toward a round dining table and dark-cabinet kitchen at Crescent',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 2 of 6 on Sister Properties. Different community from cards 1, 3–6 — describe only this card’s photo.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Crescent Apartment Homes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '8500 Maurer Rd., Lenexa, KS 66219',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.crescentaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The second-closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text.',
    context:
      'Card 2 of 6 on Sister Properties. Different community from cards 1 and 3–6. Exclude this property itself.',
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/37-dsc03809_final.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Cream sofa and orange dining chairs beside a balcony and corner fireplace at The Claremont',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 3 of 6 on Sister Properties. Different community from cards 1–2 and 4–6 — describe only this card’s photo.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'The Claremont Apartment Homes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '11909 W. 109th Street, Overland Park, KS 66210',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.claremontaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The third-closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text.',
    context:
      'Card 3 of 6 on Sister Properties. Different community from cards 1–2 and 4–6. Exclude this property itself.',
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/14-11820%20s%20greenwood%20st%20-13.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Kitchen island with built-in wine cooler opening into dining and living areas at Avignon',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 4 of 6 on Sister Properties. Different community from cards 1–3 and 5–6 — describe only this card’s photo.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Avignon Apartment Homes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '11820 S Greenwood St., Olathe, KS 66062',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.avignonapts.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The fourth-closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text.',
    context:
      'Card 4 of 6 on Sister Properties. Different community from cards 1–3 and 5–6. Exclude this property itself.',
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/05-11220%20w%20108th%20st-1.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Living room with blue accents and a kitchen pass-through at Waterford Place',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 5 of 6 on Sister Properties. Different community from cards 1–4 and 6 — describe only this card’s photo.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Waterford Place Apartments & Townhomes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '11220 W. 108th Street, Overland Park, KS 66210',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.waterfordplaceaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The fifth-closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text.',
    context:
      'Card 5 of 6 on Sister Properties. Different community from cards 1–4 and 6. Exclude this property itself.',
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800/s3/2/5458/pricebros-highlandpark-01.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Clubhouse lounge with floral wall mural and arched courtyard window at Highland Park',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Use the community name when present; describe what the photo shows.',
        context:
          'Card 6 of 6 on Sister Properties. Different community from cards 1–5 — describe only this card’s photo.',
        length: 'short phrase',
      },
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Highland Park Apartment Homes',
    },
    location: {
      label: 'Location',
      type: 'text',
      defaultValue: '10334 Goddard, Overland Park, KS 66214',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.highlandparkaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'The sixth-closest sister apartment community managed by the same company as this property, ranked by distance from this property’s address. Fill name, full street address (city, state, ZIP), official website URL, a featured exterior photo from that community’s marketing site, and alt text.',
    context:
      'Card 6 of 6 on Sister Properties. Different community from cards 1–5. Exclude this property itself.',
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

function googleMapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  const mapsHref = location ? googleMapsUrl(location) : '';

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media">
        {hasText(community.image) ? (
          <VibesitesImage
            url={community.image || ''}
            alt={community.alt || name || `Community ${number}`}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>
        {location ? (
          <a
            className="sister-card-location"
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {location}
          </a>
        ) : isEditing && !filled ? (
          <p className="sister-card-location sister-card-hint">Add city &amp; state</p>
        ) : null}

        {href ? (
          <a className="sister-card-link" href={href} target={target} rel={rel}>
            {linkLabel}
          </a>
        ) : isEditing ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Click a community card to add its photo, name, location, and website.
            Empty cards stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-location {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
          text-decoration: none;
        }
        a.sister-card-location:hover {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
